import {
  Button,
  HStack,
  NumberInput,
  NumberInputField,
  Switch,
  Text,
  Tooltip,
  useToast,
  VStack,
} from "@chakra-ui/react";
import { API, Auth } from "aws-amplify";
import { GraphQLQuery } from "@aws-amplify/api";
import { useEffect, useState } from "react";
import { useContextTypeSettingsBookkeeping } from "../dashboard-layout";
import { updateBookkeepingYear } from "../../../graphql/mutations";
import { UpdateBookkeepingYearMutation } from "../../../API";

interface BookkeepingYearBalance {
  id: string;
  year: number;
  balanceStart: string;
  originalBalanceStart: number;
  balanceStartManual: boolean;
  originalBalanceStartManual: boolean;
  balance: number;
}

const BookkeepingYears = () => {
  const toast = useToast();
  const { selectedBookkeepingFull, setSelectedBookkeepingFull } =
    useContextTypeSettingsBookkeeping();

  const [isSaving, setIsSaving] = useState(false);
  const [yearBalances, setYearBalances] = useState<BookkeepingYearBalance[]>([]);

  // Sync local state when selectedBookkeepingFull changes
  useEffect(() => {
    if (!selectedBookkeepingFull?.bookkeepingYears) {
      setYearBalances([]);
      return;
    }
    setYearBalances(
      selectedBookkeepingFull.bookkeepingYears
        .filter((year) => !year.closedAt)
        .sort((a, b) => a.year - b.year)
        .map((year) => ({
          id: year.id,
          year: year.year,
          balanceStart: String(year.balanceStart ?? 0),
          originalBalanceStart: year.balanceStart ?? 0,
          balanceStartManual: year.balanceStartManual ?? false,
          originalBalanceStartManual: year.balanceStartManual ?? false,
          balance: year.balance ?? 0,
        }))
    );
  }, [selectedBookkeepingFull]);

  const handleBalanceStartChange = (id: string, valueString: string) => {
    setYearBalances((prev) =>
      prev.map((year) =>
        year.id === id
          ? { ...year, balanceStart: valueString, balanceStartManual: true }
          : year
      )
    );
  };

  const handleManualToggle = (id: string, isManual: boolean) => {
    setYearBalances((prev) =>
      prev.map((year) =>
        year.id === id ? { ...year, balanceStartManual: isManual } : year
      )
    );
  };

  const hasChanges = yearBalances.some(
    (year) =>
      parseFloat(year.balanceStart) !== year.originalBalanceStart ||
      year.balanceStartManual !== year.originalBalanceStartManual
  );

  const saveBalanceStarts = async () => {
    setIsSaving(true);
    try {
      // Update each changed year via GraphQL
      const changedYears = yearBalances.filter(
        (year) =>
          parseFloat(year.balanceStart) !== year.originalBalanceStart ||
          year.balanceStartManual !== year.originalBalanceStartManual
      );

      for (const year of changedYears) {
        await API.graphql<GraphQLQuery<UpdateBookkeepingYearMutation>>({
          query: updateBookkeepingYear,
          variables: {
            input: {
              id: year.id,
              balanceStart: parseFloat(year.balanceStart),
              balanceStartManual: year.balanceStartManual,
            },
          },
        });
      }

      // Call reconciliation Lambda to recalculate balances
      await API.post("expenslyREST", "/accounting/balance-reconciliation", {
        headers: {
          Authorization: `Bearer ${(await Auth.currentSession())
            .getIdToken()
            .getJwtToken()}`,
        },
        body: {
          bookkeepingId: selectedBookkeepingFull?.id,
        },
      });

      // Trigger refetch to get updated values from Lambda
      setSelectedBookkeepingFull(undefined);

      toast({
        title: "Startsaldo aktualisiert",
        status: "success",
        duration: 3000,
      });
    } catch (error) {
      console.error("Error saving balance starts:", error);
      toast({
        title: "Fehler beim Speichern",
        description: "Bitte versuche es erneut.",
        status: "error",
        duration: 5000,
      });
    } finally {
      setIsSaving(false);
    }
  };

  if (!yearBalances.length) {
    return (
      <Text color="gray.500">Keine offenen Buchungsjahre vorhanden.</Text>
    );
  }

  return (
    <VStack width="full" height="full" alignItems="start" spacing="20px">
      <VStack alignItems="start" spacing="4px">
        <Text fontSize={18} fontWeight="semibold" color="gray.900">
          Kassenbuch Startsaldo
        </Text>
        <Text fontSize={14} color="gray.600">
          Hier kannst du den Startsaldo für jedes Buchungsjahr manuell anpassen.
          Nach dem Speichern werden alle Salden und Startsalden der Folgejahre
          automatisch neu berechnet.
        </Text>
      </VStack>
      <VStack alignItems="start" spacing={4} width="full" maxWidth="500px">
        <HStack width="full" justifyContent="space-between">
          <Text fontWeight="medium" width="60px">Jahr</Text>
          <Text fontWeight="medium" width="150px" textAlign="right">Startsaldo</Text>
          <Tooltip label="Manuell gesetzter Startsaldo wird nicht überschrieben">
            <Text fontWeight="medium" width="60px" textAlign="center">Manuell</Text>
          </Tooltip>
          <Text fontWeight="medium" width="150px" textAlign="right">Saldo</Text>
        </HStack>
        {yearBalances.map((year) => (
          <HStack key={year.id} width="full" justifyContent="space-between">
            <Text fontWeight="medium" width="60px">{year.year}</Text>
            <NumberInput
              value={year.balanceStart}
              onChange={(valueString) => handleBalanceStartChange(year.id, valueString)}
              isDisabled={isSaving}
              precision={2}
              step={0.01}
              width="150px"
            >
              <NumberInputField textAlign="right" />
            </NumberInput>
            <HStack width="60px" justifyContent="center">
              <Switch
                isChecked={year.balanceStartManual}
                onChange={(e) => handleManualToggle(year.id, e.target.checked)}
                isDisabled={isSaving}
                size="sm"
              />
            </HStack>
            <Text width="150px" textAlign="right" color="gray.600">
              {year.balance.toLocaleString("de-CH", {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}
            </Text>
          </HStack>
        ))}
      </VStack>
      <Button
        colorScheme="primary"
        onClick={saveBalanceStarts}
        isLoading={isSaving}
        isDisabled={isSaving || !hasChanges}
      >
        Speichern
      </Button>
    </VStack>
  );
};

export default BookkeepingYears;
